/**
 * Cross-Module Composite Risk — combines results from multiple analysis
 * modules into a unified risk score per region.
 *
 * Composite risk formula:
 * - Convergence: 30% weight
 * - CII: 50% weight
 * - Infrastructure: 20% weight
 *
 * Boosts:
 * - Theater posture: +0-20 if military surge active in region
 * - Breaking news: +0-15 if critical/high events in last 30 minutes
 *
 * Deduplication:
 * - 200km spatial merge: combine nearby risks into single zone
 * - 2h temporal dedup: don't re-alert same risk zone within 2 hours
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { AnalysisResult, CompositeRisk } from './types';
import { getEventTimestamp } from './types';
import { haversineDistance } from '@/utils/geo';
import { getTheaterByLocation } from '@/config/military';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const THIRTY_MIN_MS = 30 * 60 * 1000;
const MERGE_RADIUS_KM = 200;

// Component weights
const WEIGHT_CONVERGENCE = 0.30;
const WEIGHT_CII = 0.50;
const WEIGHT_INFRASTRUCTURE = 0.20;

// ---------------------------------------------------------------------------
// Internal state — temporal dedup
// ---------------------------------------------------------------------------

/** Track last alert time per region (approximate grid cell). */
const _lastAlertTime = new Map<string, number>();

/** Grid key for dedup: 2-degree cells. */
function gridKey(lat: number, lng: number): string {
  return `${Math.floor(lat / 2) * 2}:${Math.floor(lng / 2) * 2}`;
}

/** Check if a region is within dedup window. */
function isRecentlyAlerted(lat: number, lng: number, now: number): boolean {
  const key = gridKey(lat, lng);
  const lastTime = _lastAlertTime.get(key) ?? 0;
  return (now - lastTime) < TWO_HOURS_MS;
}

/** Record an alert for dedup. */
function recordAlert(lat: number, lng: number, now: number): void {
  _lastAlertTime.set(gridKey(lat, lng), now);
}

// ---------------------------------------------------------------------------
// Boost calculations
// ---------------------------------------------------------------------------

/**
 * Theater posture boost: +0-20 based on military activity in the region.
 * Checks if any surge events are military-type or if theater regions overlap.
 */
function theaterPostureBoost(
  lat: number,
  lng: number,
  analysisResult: AnalysisResult,
): number {
  // Check theater monitoring regions
  const theaters = getTheaterByLocation(lat, lng);
  if (theaters.length === 0) return 0;

  // Higher priority theaters get higher boost
  const maxPriority = Math.max(...theaters.map(t => t.priority));

  // Check if military surges exist
  const hasMilitarySurge = analysisResult.surges.some(
    s => s.sourceType === 'military' || s.sourceType === 'flight_position' || s.sourceType === 'vessel_position'
  );

  let boost = maxPriority * 2; // Base: 2 points per priority level
  if (hasMilitarySurge) boost += 10; // Surge adds 10

  return Math.min(20, boost);
}

/**
 * Breaking news boost: +0-15 based on critical/high events in last 30 minutes
 * near the risk zone.
 */
function breakingNewsBoost(
  lat: number,
  lng: number,
  events: NormalizedEvent[],
): number {
  const now = Date.now();
  const cutoff = now - THIRTY_MIN_MS;
  let criticalCount = 0;
  let highCount = 0;

  for (const event of events) {
    if (getEventTimestamp(event) < cutoff) continue;
    if (!event.location?.lat || !event.location?.lng) continue;

    const dist = haversineDistance(lat, lng, event.location.lat, event.location.lng);
    if (dist > MERGE_RADIUS_KM) continue;

    if (event.severity === 'critical') criticalCount++;
    else if (event.severity === 'high') highCount++;
  }

  // Critical events worth 5, high worth 2
  const boost = criticalCount * 5 + highCount * 2;
  return Math.min(15, boost);
}

// ---------------------------------------------------------------------------
// Component scoring
// ---------------------------------------------------------------------------

/**
 * Get CII score relevant to a specific location.
 * Finds nearby events to determine the country, then looks up that country's CII.
 */
function ciiComponent(
  lat: number,
  lng: number,
  events: NormalizedEvent[],
  analysisResult: AnalysisResult,
): number {
  // Find the most common country code from events near this location
  const countryCounts = new Map<string, number>();
  for (const event of events) {
    if (!event.location?.lat || !event.location?.lng || !event.location.countryCode) continue;
    const dist = haversineDistance(lat, lng, event.location.lat, event.location.lng);
    if (dist <= MERGE_RADIUS_KM) {
      const code = event.location.countryCode.toUpperCase();
      countryCounts.set(code, (countryCounts.get(code) ?? 0) + 1);
    }
  }

  if (countryCounts.size === 0) return 0;

  // Pick the country with the most nearby events
  let bestCode = '';
  let bestCount = 0;
  for (const [code, count] of countryCounts) {
    if (count > bestCount) {
      bestCount = count;
      bestCode = code;
    }
  }

  // Look up CII for that country
  const cii = analysisResult.ciiScores.get(bestCode);
  return cii?.overall ?? 0;
}

/** Get infrastructure cascade impact from analysis result. */
function infrastructureComponent(analysisResult: AnalysisResult): number {
  let totalImpact = 0;
  for (const cascade of analysisResult.cascades) {
    totalImpact = Math.max(totalImpact, Math.min(100, cascade.totalImpact * 20));
  }
  return totalImpact;
}

// ---------------------------------------------------------------------------
// Spatial merge
// ---------------------------------------------------------------------------

interface RiskCandidate {
  lat: number;
  lng: number;
  label: string;
  convergenceScore: number;
  ciiScore: number;
  infraScore: number;
}

/** Merge nearby risk candidates within MERGE_RADIUS_KM. */
function spatialMerge(candidates: RiskCandidate[]): RiskCandidate[] {
  const merged: RiskCandidate[] = [];
  const used = new Set<number>();

  for (let i = 0; i < candidates.length; i++) {
    if (used.has(i)) continue;

    const group = [candidates[i]];
    used.add(i);

    for (let j = i + 1; j < candidates.length; j++) {
      if (used.has(j)) continue;
      const dist = haversineDistance(
        candidates[i].lat, candidates[i].lng,
        candidates[j].lat, candidates[j].lng,
      );
      if (dist <= MERGE_RADIUS_KM) {
        group.push(candidates[j]);
        used.add(j);
      }
    }

    // Merge group: take highest scores and centroid location
    const avgLat = group.reduce((s, c) => s + c.lat, 0) / group.length;
    const avgLng = group.reduce((s, c) => s + c.lng, 0) / group.length;

    merged.push({
      lat: avgLat,
      lng: avgLng,
      label: group[0].label, // Use label from first (highest-scored) candidate
      convergenceScore: Math.max(...group.map(c => c.convergenceScore)),
      ciiScore: Math.max(...group.map(c => c.ciiScore)),
      infraScore: Math.max(...group.map(c => c.infraScore)),
    });
  }

  return merged;
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Calculate composite risk scores combining convergence, CII, and
 * infrastructure cascade results with military and breaking news boosts.
 *
 * @param events - All current events
 * @param analysisResult - Results from all other analysis modules
 * @returns Array of CompositeRisk zones, sorted by score descending
 */
export function calculateCompositeRisk(
  events: NormalizedEvent[],
  analysisResult: AnalysisResult,
): CompositeRisk[] {
  const now = Date.now();

  // Gather risk candidates from convergence cells
  const candidates: RiskCandidate[] = [];

  for (const conv of analysisResult.convergences) {
    candidates.push({
      lat: conv.lat,
      lng: conv.lng,
      label: conv.label,
      convergenceScore: conv.score,
      ciiScore: ciiComponent(conv.lat, conv.lng, events, analysisResult),
      infraScore: infrastructureComponent(analysisResult),
    });
  }

  // Also add high-CII countries as candidates (they may not have convergence)
  for (const [, cii] of analysisResult.ciiScores) {
    if (cii.overall >= 50) {
      // Only add if no convergence candidate already nearby
      // Use a rough lat/lng from the country name (already have events with location)
      const countryEvents = events.filter(
        e => e.location?.countryCode?.toUpperCase() === cii.countryCode
      );
      if (countryEvents.length > 0) {
        const firstWithLoc = countryEvents.find(e => e.location?.lat != null);
        if (firstWithLoc?.location) {
          candidates.push({
            lat: firstWithLoc.location.lat,
            lng: firstWithLoc.location.lng,
            label: cii.countryName,
            convergenceScore: 0,
            ciiScore: cii.overall,
            infraScore: 0,
          });
        }
      }
    }
  }

  if (candidates.length === 0) return [];

  // Spatial merge
  const merged = spatialMerge(candidates);

  // Score each merged zone
  const results: CompositeRisk[] = [];
  let riskIndex = 0;

  for (const zone of merged) {
    const convergence = zone.convergenceScore;
    const cii = zone.ciiScore;
    const infra = zone.infraScore;

    const baseScore = Math.round(
      convergence * WEIGHT_CONVERGENCE +
      cii * WEIGHT_CII +
      infra * WEIGHT_INFRASTRUCTURE
    );

    const theater = theaterPostureBoost(zone.lat, zone.lng, analysisResult);
    const breaking = breakingNewsBoost(zone.lat, zone.lng, events);

    const totalScore = Math.min(100, baseScore + theater + breaking);

    // Skip low-risk zones
    if (totalScore < 15) continue;

    // Temporal dedup
    if (isRecentlyAlerted(zone.lat, zone.lng, now)) continue;

    // Record alert for dedup
    recordAlert(zone.lat, zone.lng, now);

    results.push({
      regionId: `risk-${gridKey(zone.lat, zone.lng)}-${riskIndex++}`,
      regionName: zone.label,
      score: totalScore,
      components: {
        convergence: Math.round(convergence),
        cii: Math.round(cii),
        infrastructure: Math.round(infra),
      },
      boosts: {
        theaterPosture: theater,
        breakingNews: breaking,
      },
      location: { lat: zone.lat, lng: zone.lng },
      detectedAt: new Date().toISOString(),
    });
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results;
}
